import type { ScopeRef } from "@enduring-systems/entitlements";
import { scopeKey } from "@enduring-systems/entitlements";

import { Badge } from "@/common/ui/badge";
import { Button } from "@/common/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/common/ui/card";

import type { OfferingCatalogEntry, PendingRequestView } from "../entitlements-api";
import { scopeKindLabel } from "../scope";
import { formatRequestAge, isAged } from "./request-age";
import { RequesterText } from "./RequesterText";

/** How many of the longest-waiting requests the card lists before it stops. */
const PREVIEW_ROWS = 8;

function offeringLabel(
  request: PendingRequestView,
  offerings: readonly OfferingCatalogEntry[]
): string {
  const { offering } = request.change;
  const title =
    offerings.find((entry) => entry.name === offering.name && entry.version === offering.version)
      ?.title ?? offering.name;
  return request.change.kind === "offering"
    ? title
    : `${title}, ${String(request.change.increment)} more`;
}

function scopeLabel(scope: ScopeRef): string {
  return scope.kind === "platform"
    ? scopeKindLabel("platform")
    : `${scopeKindLabel(scope.kind)} ${scope.id}`;
}

export interface RequestQueueCardProps {
  /** The whole platform-wide queue, longest-waiting first. Null until read. */
  readonly requests: readonly PendingRequestView[] | null;
  readonly isLoading: boolean;
  readonly error: string | null;
  /** The read filled its page, so every count taken from it is a floor. */
  readonly capped: boolean;
  readonly maxAgeSeconds: number | null;
  readonly offerings: readonly OfferingCatalogEntry[];
  /** Open the workspace a request belongs to, so it can be answered there. */
  readonly onOpen: (scope: ScopeRef) => void;
}

/**
 * The whole request queue, across every workspace.
 *
 * A CS member works the queue rather than one workspace at a time, so the
 * landing page has to answer "what is waiting" before a workspace has been
 * picked. This is an ENTRY POINT and deliberately not a second decision
 * surface: pressing a row selects that workspace and hands the operator to the
 * Requests tab, where the approval's change preview is drawn from that scope's
 * own state read. Two places that could answer a request would be two places
 * that could describe one write differently.
 *
 * ## Counts here are floors when the read was capped
 *
 * The queue read is bounded, so a full page means there is more behind it. A
 * bare number then tells a CS member they have seen the whole queue when they
 * have seen a page of it, and the one thing a queue count must never do is
 * read as finished work.
 */
export function RequestQueueCard({
  requests,
  isLoading,
  error,
  capped,
  maxAgeSeconds,
  offerings,
  onOpen,
}: RequestQueueCardProps): React.ReactElement {
  const workspaces = requests === null ? 0 : new Set(requests.map((r) => scopeKey(r.scope))).size;
  /* Suffixed rather than rendered as an exact figure: with a full page read,
     both the request count and the workspace count are lower bounds. */
  const floor = capped ? "+" : "";

  return (
    <Card data-testid="request-queue-card">
      <CardHeader>
        <CardTitle>Request queue</CardTitle>
        <CardDescription>
          Everything waiting, across every workspace. Open one to approve or deny it against that
          workspace&apos;s own state.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {error !== null ? (
          <p className="text-sm text-destructive" data-testid="queue-error">
            Failed to read the request queue: {error}
          </p>
        ) : requests === null ? (
          <p className="text-sm text-muted-foreground" data-testid="queue-loading">
            {isLoading ? "Reading the request queue…" : "No request queue has been read."}
          </p>
        ) : (
          <>
            <p className="text-sm">
              <span className="font-medium tabular-nums" data-testid="queue-total">
                {String(requests.length)}
                {floor}
              </span>{" "}
              pending across{" "}
              <span className="font-medium tabular-nums" data-testid="queue-workspaces">
                {String(workspaces)}
                {floor}
              </span>{" "}
              {workspaces === 1 && !capped ? "workspace" : "workspaces"}.
            </p>

            {requests.length === 0 ? (
              <p className="text-sm text-muted-foreground" data-testid="queue-empty">
                Nothing is waiting.
              </p>
            ) : (
              <ul className="divide-y rounded-md border" data-testid="queue-list">
                {requests.slice(0, PREVIEW_ROWS).map((request) => (
                  <li
                    key={request.id}
                    className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 text-sm"
                    data-testid={`queue-row-${request.id}`}
                  >
                    <span className="flex flex-wrap items-center gap-2">
                      <span className="font-medium">{offeringLabel(request, offerings)}</span>
                      <span className="text-muted-foreground">{scopeLabel(request.scope)}</span>
                      <span className="text-muted-foreground">
                        <RequesterText request={request} testId={`requester-queue-${request.id}`} />
                      </span>
                      <span className="text-muted-foreground tabular-nums">
                        {formatRequestAge(request.ageSeconds, request.createdAt)}
                      </span>
                      {isAged(request, maxAgeSeconds) && (
                        <Badge variant="destructive" data-testid={`queue-aged-${request.id}`}>
                          Past the alarm threshold
                        </Badge>
                      )}
                    </span>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      data-testid={`queue-open-${request.id}`}
                      onClick={() => onOpen(request.scope)}
                    >
                      Open workspace
                    </Button>
                  </li>
                ))}
              </ul>
            )}

            {requests.length > PREVIEW_ROWS && (
              <p className="text-xs text-muted-foreground" data-testid="queue-truncated">
                Showing the {String(PREVIEW_ROWS)} longest-waiting of{" "}
                <span className="tabular-nums">
                  {String(requests.length)}
                  {floor}
                </span>
                .
              </p>
            )}

            {capped && (
              <p className="text-xs text-muted-foreground" data-testid="queue-capped">
                The read returned a full page, so these counts are floors rather than totals: there
                are at least this many waiting, and older requests may sit behind the cap.
              </p>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
